import { Component, EventEmitter, Input } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { Brand, Folder } from 'src/typings';
import { MarkupService } from '../markup.service';
import { Markup, MarkUpType } from '../model/markup.model';

@Component({
  selector: 'app-create',
  templateUrl: './create.component.html',
  styleUrls: ['./create.component.scss']
})
export class CreateComponent {
  constructor(private markupService: MarkupService, private fb: FormBuilder) {}

  doneEvent = new EventEmitter<void>();

  markupType = MarkUpType;

  loading = false;

  previewPrice = new FormControl<number>(100);

  createForm = this.fb.group({
    rangeStart: new FormControl<number>(0, [Validators.required]),
    rangeEnd: new FormControl<number | undefined | null>(null),
    markup: new FormControl<number>(30, [Validators.required]),
    priority: new FormControl<number>(1, [Validators.required]),
    type: new FormControl<MarkUpType>(MarkUpType.PERCENT, [Validators.required]),
    folder: new FormControl<Folder | undefined | null>(undefined),
    brands: new FormControl<Brand[]>([]),
    round: new FormControl<number>(10, [Validators.required, Validators.min(1)])
  });

  @Input() set markup(payload: Markup | undefined) {
    if (!payload) return;
    this._markup = payload;
    this.createForm.controls.rangeStart.setValue(payload.rangeStart);
    this.createForm.controls.rangeEnd.setValue(payload.rangeEnd);
    this.createForm.controls.markup.setValue(payload.markup);
    this.createForm.controls.folder.setValue(payload.folder);
    this.createForm.controls.type.setValue(payload.type);
    this.createForm.controls.priority.setValue(payload.priority);
    this.createForm.controls.round.setValue(payload.round);
    this.createForm.controls.brands.setValue(payload.brands || []);
  }

  private _markup: Markup | undefined;

  get folderControl() {
    return this.createForm.controls.folder;
  }

  get brandsControl() {
    return this.createForm.controls.brands;
  }

  get markup() {
    return this._markup;
  }

  get formTitle() {
    return this.markup ? 'Изменить правило' : 'Новое правило';
  }

  get formSubtitle() {
    return 'Глобальное правило используется, если у клиента нет своего правила. Более высокий приоритет перекрывает похожие правила.';
  }

  get isUnlimited() {
    return this.createForm.controls.rangeEnd.value === null;
  }

  get rangeSummary() {
    const rangeStart = Number(this.createForm.controls.rangeStart.value || 0);
    const rangeEnd = this.createForm.controls.rangeEnd.value;

    if (this.isUnlimited) {
      return `Итог: от ${this.formatMoney(rangeStart)} и выше`;
    }

    if (rangeEnd === null || rangeEnd === undefined || !Number.isFinite(Number(rangeEnd))) {
      return 'Итог: укажите цену до или включите "Нет верхней границы"';
    }

    return `Итог: от ${this.formatMoney(rangeStart)} до ${this.formatMoney(Number(rangeEnd))} включительно`;
  }

  get rangeError() {
    const rangeEnd = this.createForm.controls.rangeEnd.value;
    return rangeEnd !== null && rangeEnd !== undefined && Number(rangeEnd) < Number(this.createForm.controls.rangeStart.value);
  }

  get rangeEndMissing() {
    const rangeEnd = this.createForm.controls.rangeEnd.value;
    return !this.isUnlimited && (rangeEnd === null || rangeEnd === undefined);
  }

  get roundError() {
    return Number(this.createForm.controls.round.value) <= 0;
  }

  get typeHelpText() {
    if (this.createForm.controls.type.value === MarkUpType.FIXED) {
      return 'Например, 100 = добавить к закупке 100 грн.';
    }
    return 'Например, 30 = добавить к закупке 30%.';
  }

  get conditionsSummary() {
    const folder = this.createForm.controls.folder.value;
    const brands = this.createForm.controls.brands.value || [];
    const parts: string[] = [];

    if (folder) parts.push(`папка "${folder.description}"`);
    if (brands.length === 1) parts.push(`бренд "${brands[0].description}"`);
    if (brands.length > 1) parts.push(`брендов: ${brands.length}`);

    if (!parts.length) return 'Сейчас правило действует на все товары.';
    return `Сейчас правило ограничено: ${parts.join(', ')}.`;
  }

  get previewText() {
    const price = Number(this.previewPrice.value || 0);
    const result = this.calculatePreview(price);
    if (!Number.isFinite(price) || price <= 0 || result === null) {
      return 'Введите корректную закупку и правило';
    }
    return `${this.formatMoney(price)} -> ${this.formatMoney(result)}`;
  }

  get previewSaleText() {
    const price = Number(this.previewPrice.value || 0);
    const result = this.calculatePreview(price);
    if (!Number.isFinite(price) || price <= 0 || result === null) {
      return 'Не рассчитано';
    }
    return this.formatMoney(result);
  }

  get previewCalculationText() {
    const price = Number(this.previewPrice.value || 0);
    const markup = Number(this.createForm.controls.markup.value || 0);
    const round = Number(this.createForm.controls.round.value || 0);

    if (!Number.isFinite(price) || price <= 0) {
      return 'Введите закупочную цену больше 0.';
    }

    if (!Number.isFinite(round) || round <= 0) {
      return 'Округление должно быть больше 0.';
    }

    const markupText = this.createForm.controls.type.value === MarkUpType.FIXED
      ? `+${this.formatMoney(markup)}`
      : `+${this.formatNumber(markup)}%`;

    return `Закупка ${this.formatMoney(price)}, ${markupText}, округление до ${this.formatNumber(round)} грн.`;
  }

  get previewRangeState() {
    const price = Number(this.previewPrice.value || 0);
    const rangeStart = Number(this.createForm.controls.rangeStart.value || 0);
    const rangeEnd = this.createForm.controls.rangeEnd.value;

    if (!Number.isFinite(price) || price <= 0) return 'neutral';
    if (!Number.isFinite(rangeStart) || this.rangeEndMissing) return 'neutral';
    if (price < rangeStart) return 'warning';
    if (!this.isUnlimited && rangeEnd !== null && rangeEnd !== undefined && price > Number(rangeEnd)) {
      return 'warning';
    }

    return 'success';
  }

  get previewRangeNoticeText() {
    const price = Number(this.previewPrice.value || 0);
    const rangeStart = Number(this.createForm.controls.rangeStart.value || 0);
    const rangeEnd = this.createForm.controls.rangeEnd.value;

    if (!Number.isFinite(price) || price <= 0) {
      return 'Введите закупочную цену для проверки диапазона.';
    }

    if (price < rangeStart) {
      return `Закупка ${this.formatMoney(price)} ниже начала ${this.formatMoney(rangeStart)}: правило не сработает.`;
    }

    if (!this.isUnlimited && rangeEnd !== null && rangeEnd !== undefined && price > Number(rangeEnd)) {
      return `Закупка ${this.formatMoney(price)} выше верхней границы ${this.formatMoney(Number(rangeEnd))}: правило не сработает.`;
    }

    return `Закупка ${this.formatMoney(price)} попадает в диапазон правила.`;
  }

  toggleUnlimited(checked: boolean) {
    this.createForm.controls.rangeEnd.setValue(checked ? null : undefined);
  }

  async create() {
    const payload: any = { ...this.createForm.value };
    await firstValueFrom(this.markupService.create(payload));
  }

  async update() {
    if (!this.markup) return;
    await firstValueFrom(
      this.markupService.update({ ...this.createForm.value, id: this.markup.id })
    );
  }

  async save() {
    if (this.loading || this.createForm.invalid || this.rangeError || this.roundError || this.rangeEndMissing) return;
    this.loading = true;
    try {
      if (this.markup) {
        await this.update();
      } else {
        await this.create();
      }
      this.doneEvent.emit();
    } finally {
      this.loading = false;
    }
  }

  private calculatePreview(price: number) {
    const round = Number(this.createForm.controls.round.value || 0);
    const markup = Number(this.createForm.controls.markup.value || 0);
    if (!Number.isFinite(price) || !Number.isFinite(round) || round <= 0) return null;

    if (this.createForm.controls.type.value === MarkUpType.FIXED) {
      return Math.round((price + markup) / round) * round;
    }
    return Math.round((price * (markup / 100 + 1)) / round) * round;
  }

  private formatMoney(value: number) {
    return `${new Intl.NumberFormat('ru-RU', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(Number(value || 0))} грн`;
  }

  private formatNumber(value: number) {
    return new Intl.NumberFormat('ru-RU', {
      maximumFractionDigits: 2
    }).format(Number(value || 0));
  }
}
